// Retro session state. v1 is backendless, so what the original spec calls
// "server-authoritative" lives on the doc's `meta` Y.Map and is written by
// the facilitator (the host who created the board). Every peer reads it
// through CRDT sync — no separate message channel.
//
// Y.Doc layout:
//   doc.getMap('meta')   → BoardMode + RetroPhase + RetroTimer + voteBudget
//   doc.getMap('votes')  → Map<shapeId, Map<userId, number>>
//   doc.getMap('shapes') → existing shapes map (see shapes.ts)

export type BoardMode = 'freeform' | 'retro' | 'mindmap' | 'systemmap';

// Phase order matches the canonical retro flow. `lobby` is pre-start;
// `archived` is post-session (board becomes read-mostly).
export type RetroPhase =
  | 'lobby'
  | 'write'
  | 'reveal'
  | 'group'
  | 'vote'
  | 'discuss'
  | 'actions'
  | 'archived';

export const RETRO_PHASES: readonly RetroPhase[] = [
  'lobby',
  'write',
  'reveal',
  'group',
  'vote',
  'discuss',
  'actions',
  'archived',
];

// Phases that appear in the top-bar phase pills. We hide `archived` from the
// active stepper — once a retro is archived it's done; getting "back" to an
// active phase is rare enough to live in a separate menu rather than as a pill.
export const ACTIVE_PHASES: readonly RetroPhase[] = [
  'lobby',
  'write',
  'reveal',
  'group',
  'vote',
  'discuss',
  'actions',
];

// Human-friendly labels for the phase pills.
export const PHASE_LABELS: Readonly<Record<RetroPhase, string>> = {
  lobby: 'Lobby',
  write: 'Write',
  reveal: 'Reveal',
  group: 'Group',
  vote: 'Vote',
  discuss: 'Discuss',
  actions: 'Actions',
  archived: 'Archived',
};

// Server-authoritative-in-spirit timer. The facilitator computes `endsAt`
// locally (Date.now() + durationMs) and writes the snapshot to meta; every
// peer renders the countdown via a 1Hz interval against their own clock.
// Pause snapshots `pausedAt`; resume shifts `endsAt` by (now - pausedAt).
//
// Clock skew between peers is bounded by sync latency (sub-second on WebRTC
// mesh). Worst-case the countdown drifts by ~200ms across peers — fine for
// the use case (5-minute retro rounds).
export interface RetroTimer {
  endsAt: number;
  durationMs: number;
  paused: boolean;
  pausedAt?: number;
}

export interface RetroMeta {
  mode: BoardMode;
  phase?: RetroPhase;
  // Facilitator-only switch. While false in `write` phase, peers see
  // other-author stickies with placeholder text (• • •) so participants
  // can't anchor on what others wrote. Once true, content unveils.
  revealed?: boolean;
  timer?: RetroTimer;
  // Per-participant cap on dot votes during the `vote` phase.
  voteBudget?: number;
}

// Default budget per the spec (§9). Override per-board via setVoteBudget.
export const DEFAULT_VOTE_BUDGET = 3;

// Convenience: which phase comes after the current one (for "advance" UX).
// Past `actions` returns `archived`; past `archived` returns `archived` (the
// terminal state).
export function nextPhase(phase: RetroPhase): RetroPhase {
  const i = RETRO_PHASES.indexOf(phase);
  if (i === -1 || i === RETRO_PHASES.length - 1) return 'archived';
  return RETRO_PHASES[i + 1]!;
}

export function prevPhase(phase: RetroPhase): RetroPhase {
  const i = RETRO_PHASES.indexOf(phase);
  if (i <= 0) return 'lobby';
  return RETRO_PHASES[i - 1]!;
}
